import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router";
import mixpanel from "mixpanel-browser";

import { auth, getUser } from "_firebase";
import { AuthContext } from "features/Auth";

import { getEffectiveEmailConfirmed } from "utils/user/verification";

import Button from "components/Button";
import Step1 from "./step1";
import Step2 from "./step2";

import classes from "./styles.module.scss";

import VoltaLogo from "assets/images/logo-color.svg";

const RESEND_COOLDOWN = 60;

// the auth flag is firebase's own, isEmailConfirmed is the copy the rest of the app reads,
// and the gate stays up until they agree
const isEmailVerified = (authUser, userData) =>
    Boolean(authUser?.emailVerified) && getEffectiveEmailConfirmed(userData || {});

const VerifyEmail = ({ email }) => {
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown === 0) return;

        const timeout = setTimeout(() => setCooldown((prev) => prev - 1), 1000);

        return () => clearTimeout(timeout);
    }, [cooldown]);

    const handleResend = async () => {
        try {
            await auth.currentUser.sendEmailVerification();
            setCooldown(RESEND_COOLDOWN);
        } catch (error) {
            console.error("Error resending email verification", error);
        }
    };

    return (
        <div className={classes.verifyEmail}>
            <h2>Confirm your email</h2>
            <span>
                We sent a confirmation link to <strong>{email}</strong>. Open it to continue your registration.
            </span>
            <span>This page continues on its own once the address is confirmed.</span>
            <Button type="hollow" onClick={handleResend} disabled={cooldown > 0} style={{ width: "100%" }}>
                Resend email
            </Button>
            {cooldown > 0 && <span className={classes.resendTimer}>You can resend in {cooldown}s</span>}
        </div>
    );
};

export default ({ signUpWithEmailAndPassword, error, createUser }) => {
    const { setUser: setAuthUser, user: contextUser } = useContext(AuthContext);

    const [step, setStep] = useState(1);
    const [processing, toggleProcessing] = useState(false);
    const [resuming, setResuming] = useState(true);
    const [user, setUser] = useState(null);
    const [registrationDone, setRegistrationDone] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const currentUser = auth.currentUser;

            if (currentUser) {
                try {
                    const userData = await getUser(currentUser.uid);

                    setUser(userData);
                    setEmailVerified(isEmailVerified(currentUser, userData));

                    if (!cancelled && userData?.registrationComplete === false) {
                        setStep(2);
                        mixpanel.track("Sign Up Step 2", {
                            UserId: currentUser.uid,
                            Resumed: true,
                        });
                    }
                } catch (error) {
                    console.error("Error checking incomplete registration", error);
                }
            }

            if (!cancelled) setResuming(false);
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    // emailVerified on the cached auth user never changes on its own, and isEmailConfirmed is
    // written by the confirmation link handler, so both get reread while the gate is up
    useEffect(() => {
        if (step !== 2 || emailVerified) return;

        const recheck = async () => {
            try {
                await auth.currentUser?.reload();

                const userData = await getUser(auth.currentUser?.uid);

                if (userData) setUser(userData);
                if (isEmailVerified(auth.currentUser, userData)) setEmailVerified(true);
            } catch (error) {
                console.error("Error refreshing email verification state", error);
            }
        };

        recheck();
        const interval = setInterval(recheck, 5000);

        return () => clearInterval(interval);
    }, [step, emailVerified]);

    const handleCreateUser = async (event, regForm) => {
        event.preventDefault();
        toggleProcessing(true);

        const {
            email: { value: email },
            password: { value: password },
        } = regForm;

        if (!email || !password) {
            toggleProcessing(false);
            return;
        }

        const {
            error = null,
            canMoveToNextStep = false,
            user = null,
        } = await signUpWithEmailAndPassword(email, password);

        if (error) {
            toggleProcessing(false);
            return;
        }

        setUser(user);

        if (canMoveToNextStep) {
            mixpanel.track("Sign Up Step 1", {
                UserId: user?.uid,
                Email: email,
            });
            setStep(2);
            mixpanel.track("Sign Up Step 2", {
                UserId: user?.uid,
                Resumed: false,
            });
        }

        toggleProcessing(false);
    };

    const handleUserDetails = async (event, accountDetails, teamToCreate) => {
        toggleProcessing(true);

        const newAccountDetails = {};

        Object.entries(accountDetails).forEach(([key, { value }]) => {
            if (key === "profession") {
                newAccountDetails["isRea"] = value.includes("registeredEnergyAdvisor");
            }
            newAccountDetails[key] = value;
        });

        const result = await createUser(event, user, newAccountDetails, teamToCreate);

        if (result?.success) {
            mixpanel.track("Sign Up Complete", {
                UserId: user?.uid,
                ReqTeam: Boolean(teamToCreate),
                IsRea: Boolean(newAccountDetails.isRea),
                Profession: newAccountDetails.profession,
            });

            setAuthUser(auth.currentUser);
            localStorage.setItem("wasLoggedIn", "true");
            setRegistrationDone(true);
        }

        toggleProcessing(false);
    };

    if (registrationDone || user?.registrationComplete) {
        return <Redirect to="/dashboard" />;
    }

    if (resuming) {
        return null;
    }

    return (
        <div className={classes.authWrapper}>
            <div className={classes.stepsWrapper}>
                <img
                    src={VoltaLogo}
                    style={{ height: "30px", alignSelf: "flex-start" }}
                    alt="Volta Snap logo in colour"
                />
                {step === 1 && <Step1 createUser={handleCreateUser} error={error} processing={processing} />}
                {step === 2 && !emailVerified && <VerifyEmail email={user?.email} />}
                {step === 2 && emailVerified && <Step2 handleUserDetails={handleUserDetails} processing={processing} />}
            </div>
        </div>
    );
};
